import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { SITE, PROPERTIES } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  property_interest: "",
  arrival_date: "",
  departure_date: "",
  guests: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guests: form.guests ? Number(form.guests) : undefined,
      };
      await axios.post(`${API}/enquiries`, payload);
      setSent(true);
      setForm(initialForm);
      toast.success("Thank you — we'll be in touch shortly.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send enquiry. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 sm:py-32" style={{ background: "var(--dl-bg-2)" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="section-eyebrow">
            <span className="divider-rule" /> Get in touch
          </p>
          <h2
            className="mt-5 font-serif text-4xl sm:text-5xl tracking-tight leading-[1.05]"
            style={{ color: "var(--dl-ink)", fontWeight: 400 }}
          >
            Book a relaxing break
            <br />
            <span className="serif-italic">in Cheshire today.</span>
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed" style={{ color: "var(--dl-ink-2)" }}>
            Send us a note about your dates, the property you have in mind, and any
            questions. We typically reply within a day.
          </p>

          <ul className="mt-10 space-y-5">
            <li className="flex items-start gap-4" data-testid="contact-phone">
              <Phone size={18} style={{ color: "var(--dl-accent)" }} />
              <div>
                <p className="overline">Phone</p>
                <a href={SITE.phoneHref} className="font-serif text-2xl" style={{ color: "var(--dl-ink)" }}>
                  {SITE.phone}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-whatsapp">
              <MessageCircle size={18} style={{ color: "var(--dl-accent)" }} />
              <div>
                <p className="overline">WhatsApp</p>
                <a
                  href={SITE.whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-lg"
                  style={{ color: "var(--dl-ink)" }}
                >
                  Message us on WhatsApp
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-email">
              <Mail size={18} style={{ color: "var(--dl-accent)" }} />
              <div>
                <p className="overline">Email</p>
                <a href={SITE.emailHref} className="text-lg" style={{ color: "var(--dl-ink)" }}>
                  {SITE.email}
                </a>
              </div>
            </li>
            <li className="flex items-start gap-4" data-testid="contact-address">
              <MapPin size={18} style={{ color: "var(--dl-accent)" }} />
              <div>
                <p className="overline">Address</p>
                <p className="text-lg" style={{ color: "var(--dl-ink)" }}>{SITE.address}</p>
              </div>
            </li>
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="enquiry-form"
          className="lg:col-span-7 bg-white p-8 sm:p-12 border"
          style={{ borderColor: "var(--dl-border)" }}
        >
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
            <div>
              <label className="field-label">Your name *</label>
              <input
                data-testid="form-name"
                className="dl-input"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Jane Smith"
                required
              />
            </div>
            <div>
              <label className="field-label">Email *</label>
              <input
                data-testid="form-email"
                className="dl-input"
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label className="field-label">Phone</label>
              <input
                data-testid="form-phone"
                className="dl-input"
                name="phone"
                value={form.phone}
                onChange={onChange}
                placeholder="07000 000000"
              />
            </div>
            <div>
              <label className="field-label">Property</label>
              <select
                data-testid="form-property"
                className="dl-input"
                name="property_interest"
                value={form.property_interest}
                onChange={onChange}
              >
                <option value="">Any / not sure</option>
                {PROPERTIES.map((p) => (
                  <option key={p.id} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Arrival</label>
              <input
                data-testid="form-arrival"
                className="dl-input"
                type="date"
                name="arrival_date"
                value={form.arrival_date}
                onChange={onChange}
              />
            </div>
            <div>
              <label className="field-label">Departure</label>
              <input
                data-testid="form-departure"
                className="dl-input"
                type="date"
                name="departure_date"
                value={form.departure_date}
                onChange={onChange}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Number of guests</label>
              <input
                data-testid="form-guests"
                className="dl-input"
                type="number"
                min="1"
                max="20"
                name="guests"
                value={form.guests}
                onChange={onChange}
                placeholder="2"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Message *</label>
              <textarea
                data-testid="form-message"
                className="dl-input"
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="Tell us a bit about your trip — anniversary, family weekend, working stay, dogs welcome?"
                required
              />
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between gap-6 flex-wrap">
            <p className="text-xs max-w-xs" style={{ color: "var(--dl-muted)" }}>
              By submitting you agree we may contact you about your enquiry. No marketing — we promise.
            </p>
            <button
              type="submit"
              data-testid="form-submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-60"
            >
              {sent ? (
                <>
                  Sent <Check size={16} />
                </>
              ) : submitting ? (
                "Sending..."
              ) : (
                <>
                  Send Enquiry <Send size={14} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
